import React from 'react';
import { debounce } from 'lodash';

import { logger } from '@neptune/shared/core-logger-util';

interface UseDebounceControlledInputValueProps {
  debouncedValue: string;
  debounceDelayMs?: number;
  onValueChange: (value: string) => void;
}

interface EmittedValueSnapshot {
  localChangeSequence: number;
  emittedAtMs: number;
  value: string;
}

interface PendingValueConflictInput {
  pendingValue: string | null;
  pendingLocalChangeSequence: number | null;
  debouncedValue: string;
  lastEmittedValueSnapshot: EmittedValueSnapshot | null;
  lastLocalValueChangeAtMs: number;
}

const shouldPreferPendingValueOverControlledValue = ({
  pendingValue,
  pendingLocalChangeSequence,
  debouncedValue,
  lastEmittedValueSnapshot,
  lastLocalValueChangeAtMs,
}: PendingValueConflictInput): boolean => {
  if (pendingValue === null || pendingLocalChangeSequence === null) {
    return false;
  }

  if (lastEmittedValueSnapshot === null) {
    return false;
  }

  if (lastEmittedValueSnapshot.value !== debouncedValue) {
    return false;
  }

  if (pendingValue === debouncedValue) {
    return false;
  }

  const pendingSequenceIsNewer =
    pendingLocalChangeSequence > lastEmittedValueSnapshot.localChangeSequence;
  const pendingTimestampIsNewer = lastLocalValueChangeAtMs >= lastEmittedValueSnapshot.emittedAtMs;

  return pendingSequenceIsNewer || pendingTimestampIsNewer;
};

export const useDebounceControlledInputValue = ({
  debouncedValue,
  onValueChange,
  debounceDelayMs = 300,
}: UseDebounceControlledInputValueProps) => {
  const [value, setValue] = React.useState(debouncedValue);

  const localChangeSequenceRef = React.useRef<number>(0);
  const lastLocalValueChangeAtMsRef = React.useRef<number>(0);
  const pendingLocalChangeSequenceRef = React.useRef<number | null>(null);
  const lastEmittedValueSnapshotRef = React.useRef<EmittedValueSnapshot | null>(null);
  const pendingValueRef = React.useRef<string | null>(null);
  const onValueChangeRef = React.useRef(onValueChange);
  const previousOnValueChangeRef = React.useRef(onValueChange);
  const debouncedOnValueChangeRef = React.useRef<ReturnType<typeof debounce>>();

  const debouncedOnValueChange = React.useMemo(() => {
    return debounce((newValue: string) => {
      const emittedLocalChangeSequence = pendingLocalChangeSequenceRef.current ?? 0;

      if (pendingValueRef.current === newValue) {
        pendingValueRef.current = null;
        pendingLocalChangeSequenceRef.current = null;
      }

      lastEmittedValueSnapshotRef.current = {
        localChangeSequence: emittedLocalChangeSequence,
        emittedAtMs: Date.now(),
        value: newValue,
      };
      onValueChangeRef.current(newValue);
    }, debounceDelayMs);
  }, [debounceDelayMs]);

  React.useEffect(() => {
    debouncedOnValueChangeRef.current = debouncedOnValueChange;
  }, [debouncedOnValueChange]);

  React.useEffect(() => {
    const callbackChanged = previousOnValueChangeRef.current !== onValueChange;
    const hasPendingLocalChange =
      pendingValueRef.current !== null && pendingLocalChangeSequenceRef.current !== null;

    if (callbackChanged && hasPendingLocalChange) {
      logger.logMessage('useDebounceControlledInputValue: callback reference changed', {
        module: 'use-debounce-controlled-input-value',
        hasPendingLocalChange: true,
      });
    }

    onValueChangeRef.current = onValueChange;
    previousOnValueChangeRef.current = onValueChange;
  }, [onValueChange]);

  const handleValueChange = React.useCallback(
    (newValue: string) => {
      if (newValue !== value) {
        localChangeSequenceRef.current += 1;
        setValue(newValue);
        lastLocalValueChangeAtMsRef.current = Date.now();
        pendingValueRef.current = newValue;
        pendingLocalChangeSequenceRef.current = localChangeSequenceRef.current;
        debouncedOnValueChange(newValue);
      }
    },
    [debouncedOnValueChange, value],
  );

  React.useEffect(() => {
    const pendingValue = pendingValueRef.current;
    const pendingLocalChangeSequence = pendingLocalChangeSequenceRef.current;
    const lastEmittedValueSnapshot = lastEmittedValueSnapshotRef.current;
    const preferPendingValue = shouldPreferPendingValueOverControlledValue({
      pendingValue,
      pendingLocalChangeSequence,
      debouncedValue,
      lastEmittedValueSnapshot,
      lastLocalValueChangeAtMs: lastLocalValueChangeAtMsRef.current,
    });

    debouncedOnValueChangeRef.current?.cancel();

    if (preferPendingValue) {
      logger.logMessage('useDebounceControlledInputValue: preferring pending value', {
        module: 'use-debounce-controlled-input-value',
        debouncedValue,
        pendingValue,
        value,
      });

      debouncedOnValueChangeRef.current?.(pendingValue);
    } else {
      setValue(debouncedValue);
      pendingValueRef.current = null;
      pendingLocalChangeSequenceRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValue]);

  React.useEffect(() => {
    return () => {
      debouncedOnValueChange.cancel();
    };
  }, [debouncedOnValueChange]);

  return { value, handleValueChange };
};

export default useDebounceControlledInputValue;
